import { numeralFormatterCurrency, formatDecimalAsPercentage } from 'ns_libs/formatter';
import { ProFormaModelIds } from 'constants/proForma';
import { SensitivityAnalysisParameter } from '../../types';
import { SensitivityValue } from '../SensitivityTable/types';

export const GROSS_RENT_LABEL = 'gross rent';
export const HORIZONTAL = 'horizontal';
export const VERTICAL = 'vertical';

// Sensitivity ID mapping for compact storage - IDs are arbitrary but serve to save the least locally as possible
export const SENSITIVITY_ID_MAP = {
    1: 'Exit Capitalization Rate vs Gross Rent',
    2: 'Gross Rent vs Vacancy Rate',
    3: 'Lease Up Pace vs Gross Rent',
    4: 'Operating Expenses vs Gross Rent',
    5: 'Exit Capitalization Rate vs Hard and Soft Costs',
    6: 'Interest Rate vs Loan to Cost',
    7: 'Acquisition Price vs Exit Capitalization Rate',
} as const;

export const SENSITIVITY_TYPE_TO_ID_MAP = Object.fromEntries(
    Object.entries(SENSITIVITY_ID_MAP).map(([id, type]) => [type, id]),
) as Record<string, string>;

export const getSensitivityIdFromType = (type: string): string | undefined => (
    SENSITIVITY_TYPE_TO_ID_MAP[type]
);

export const getSensitivityTypeFromId = (id: string): string | undefined => (
    SENSITIVITY_ID_MAP[Number(id) as keyof typeof SENSITIVITY_ID_MAP]
);

export const isValidSensitivityId = (id: string): boolean => (
    typeof id === 'string' && Number(id) in SENSITIVITY_ID_MAP
);

export const getSensitivityConfigById = (id: string, proFormaModelId?: number) => {
    const type = getSensitivityTypeFromId(id);
    if (!type) return null;

    const option = getSensitivityOptions(proFormaModelId).find(opt => opt.value === type);
    if (!option) return null;

    return {
        id: id === '1' ? 'main' : `table-${Date.now()}-${id}`,
        type,
        tableLabel: option.label,
    };
};

export const getGrossRentLabel = (proFormaModelId?: number) => {
    if (proFormaModelId === ProFormaModelIds.RETAIL
        || proFormaModelId === ProFormaModelIds.OFFICE
        || proFormaModelId === ProFormaModelIds.INDUSTRIAL
        || proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        return 'Total rental income';
    }
    return 'Gross rent';
};

export const getSensitivityConfig = (showCents: boolean = false) => {
    const currencyFormat = showCents ? '$0,0.00' : '$0,0';
    const formatter = (value: number | string | undefined) => numeralFormatterCurrency(value, currencyFormat);

    return {
        'Gross rent vs Vacancy rate': {
            displayValue: 'netOperatingIncome',
            formatter,
        },
        'Exit cap rate vs Gross rent': {
            displayValue: 'exitValuation',
            formatter,
        },
        'Lease-up pace vs Gross rent': {
            displayValue: 'netOperatingIncome',
            formatter,
        },
        'Operating expenses vs Gross rent': {
            displayValue: 'netOperatingIncome',
            formatter,
        },
        'Exit cap rate vs Hard + soft costs': {
            displayValue: 'exitValuation',
            formatter,
        },
        'Interest rate vs LTC': {
            displayValue: 'constructionDebtAmount',
            formatter,
        },
        'Acquisition price vs Exit cap rate': {
            displayValue: 'leveredIRR',
            formatter: (value: number | string | undefined) => (value ? formatDecimalAsPercentage(value, 2) : ''),
        },
    } as const;
};

type SensitivityOption = {
    excludedModels: ProFormaModelIds[]
    value: SensitivityAnalysisParameter;
    label: string;
    verticalLabel: string;
    horizontalLabel: string;
    increments: { vertical: number; horizontal: number; }
}

export const getSensitivityOptions = (proFormaModelId?: ProFormaModelIds): SensitivityOption[] => {
    const allOptions: SensitivityOption[] = [
        {
            excludedModels: [],
            value: 'Gross Rent vs Vacancy Rate',
            label: `${getGrossRentLabel(proFormaModelId)} vs Vacancy rate`,
            verticalLabel: getGrossRentLabel(proFormaModelId),
            horizontalLabel: 'Vacancy rate',
            increments: { vertical: 0.01, horizontal: 0.01 },
        },
        {
            excludedModels: [],
            value: 'Exit Capitalization Rate vs Gross Rent',
            label: `Exit cap rate vs ${getGrossRentLabel(proFormaModelId)}`,
            verticalLabel: 'Exit cap rate',
            horizontalLabel: getGrossRentLabel(proFormaModelId),
            increments: { vertical: 0.001, horizontal: 0.01 },
        },
        {
            excludedModels: [ProFormaModelIds.STUDENT_HOUSING],
            value: 'Lease Up Pace vs Gross Rent',
            label: `Lease-up pace vs ${getGrossRentLabel(proFormaModelId)}`,
            verticalLabel: 'Lease-up pace',
            horizontalLabel: getGrossRentLabel(proFormaModelId),
            increments: { vertical: 1, horizontal: 0.01 },
        },
        {
            excludedModels: [],
            value: 'Operating Expenses vs Gross Rent',
            label: `Operating expenses vs ${getGrossRentLabel(proFormaModelId)}`,
            verticalLabel: 'Operating expenses',
            horizontalLabel: getGrossRentLabel(proFormaModelId),
            increments: { vertical: 0.01, horizontal: 0.01 },
        },
        {
            excludedModels: [],
            value: 'Exit Capitalization Rate vs Hard and Soft Costs',
            label: 'Exit cap rate vs Hard + soft costs',
            verticalLabel: 'Exit cap rate',
            horizontalLabel: 'Hard + soft costs',
            increments: { vertical: 0.001, horizontal: 0.01 },
        },
        {
            excludedModels: [],
            value: 'Interest Rate vs Loan to Cost',
            label: 'Interest rate vs LTC',
            verticalLabel: 'Interest rate',
            horizontalLabel: 'LTC',
            increments: { vertical: 0.001, horizontal: 0.01 },
        },
        {
            excludedModels: [],
            value: 'Acquisition Price vs Exit Capitalization Rate',
            label: 'Acquisition price vs Exit cap rate',
            verticalLabel: 'Acquisition price',
            horizontalLabel: 'Exit cap rate',
            increments: { vertical: 0.01, horizontal: 0.001 },
        },
    ];

    return allOptions.filter(option => !proFormaModelId || !option.excludedModels.includes(proFormaModelId));
};

export const LOAN_TO_COST = 'Loan to Cost';
export const LOAN_TO_VALUE = 'Loan to Value';
export const LEASE_UP_PACE = 'Lease Up Pace';
export const INTEREST_RATE_VS_LTV = 'Interest Rate vs LTV';

export const EMPTY_DATA_VALUES = [
    { status: 'best' }, { status: 'best' }, { status: 'good' }, { status: 'good' }, { status: 'good' },
    { status: 'best' }, { status: 'best' }, { status: 'good' }, { status: 'good' }, { status: 'good' },
    { status: 'good' }, { status: 'good' }, { status: 'good' }, { status: 'good' }, { status: 'good' },
    { status: 'good' }, { status: 'good' }, { status: 'good' }, { status: 'warning' }, { status: 'warning' },
    { status: 'good' }, { status: 'good' }, { status: 'good' }, { status: 'warning' }, { status: 'danger' },
] as SensitivityValue[];
